import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { notificar } from '../utils/notify';
import { buscarCredencial, copiarParaClipboard } from '../utils/credenciaisBpoRh';

// Multi-select com checkboxes — mesmo padrão da página Contabilidade
const MultiSelectDropdown = ({ options, selected, onChange, placeholder = 'Todos' }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggle = (val) => {
    onChange(selected.includes(val) ? selected.filter(v => v !== val) : [...selected, val]);
  };

  const label = selected.length === 0
    ? placeholder
    : selected.length === 1
    ? selected[0]
    : `${selected.length} selecionados`;

  return (
    <div ref={ref} style={{ position: 'relative' }} className="mt-1">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="form-select form-select-sm text-start"
        style={{ paddingRight: '2rem', background: selected.length > 0 ? '#e8f4ff' : undefined, borderColor: selected.length > 0 ? '#0d6efd' : undefined }}
      >
        <span style={{ fontSize: '0.8rem' }}>{label}</span>
      </button>
      {open && (
        <div style={{
          position: 'absolute', zIndex: 1050, background: 'white',
          border: '1px solid #dee2e6', borderRadius: '0.375rem',
          boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
          minWidth: '200px', maxHeight: '260px', overflowY: 'auto',
          top: 'calc(100% + 2px)', left: 0,
        }}>
          {selected.length > 0 && (
            <div
              onClick={() => onChange([])}
              className="px-3 py-2 border-bottom text-danger"
              style={{ cursor: 'pointer', fontSize: '0.8rem' }}
            >
              <i className="fas fa-times me-1"></i> Limpar seleção
            </div>
          )}
          {options.map(opt => (
            <label
              key={opt}
              className="d-flex align-items-center px-3 py-2 gap-2 m-0"
              style={{ cursor: 'pointer', fontSize: '0.85rem', borderBottom: '1px solid #f8f9fa' }}
            >
              <input
                type="checkbox"
                checked={selected.includes(opt)}
                onChange={() => toggle(opt)}
                style={{ cursor: 'pointer' }}
              />
              {opt}
            </label>
          ))}
        </div>
      )}
    </div>
  );
};

const FORM_VAZIO = {
  id: null,
  cliente_id: '',
  categoria: '',
  propriedade: '',
  portal: '',
  login: '',
  senha: '',
  email_reset: '',
  api_token: '',
  // vindas da listagem (`tem_*`): dizem se já existe credencial salva, sem trazer o valor
  tem_senha: false,
  tem_api_token: false,
};

// Espelha a regra do backend (_ponto_disponiveis): o cliente só tem "ponto
// ativo" quando a propriedade é um sistema que a gente integra E as credenciais
// exigidas por ele estão preenchidas. Ex.: um PontoMais sem senha não conecta —
// nesses casos não mostramos o botão Ponto.
const pontoAtivo = (servico) => {
  const pl = (servico.propriedade || '').toLowerCase();
  if (pl.includes('ponto mais')) return !!servico.login && !!servico.tem_senha;
  if (pl.includes('tiquetaque')) return !!servico.tem_api_token;
  if (pl.includes('rhid')) return !!servico.login && !!servico.tem_senha;
  // Kairos não tem API de batidas, mas o painel funciona pela importação do
  // Excel do "Relatório de Ponto do Funcionário" — então é ponto ativo.
  if (pl.includes('kairos')) return !!servico.tem_api_token;
  // Ahgora/Aghora: sem API nenhuma — o painel funciona pela importação da
  // planilha (CSV/Excel), então basta o serviço estar cadastrado.
  if (pl.includes('aghora') || pl.includes('ahgora')) return true;
  return false;
};

// serviços de ponto SEM API: a coluna "API Ponto" explica de onde vêm os dados
// Espelha _VA_PROVEDORES no backend: a categoria 'Benefícios' também guarda
// Alura, Wellhub e afins, que não têm controle de crédito no app.
const VA_PROVEDORES = ['caju', 'flash', 'swile', 'pluxee', 'alelo',
  'sodexo', 'ticket', 'ifood', 'vr', 'ben'];
const ehVA = (servico) => (servico.propriedade || '').toLowerCase().split(/\s+/)
  .some(p => VA_PROVEDORES.includes(p));

// as duas grafias convivem na base (o cadastro de um cliente veio com typo)
const ehHomeOffice = (cat) => ['home office', 'home oficce'].includes(
  (cat || '').trim().toLowerCase());

// convênios de farmácia ficam na categoria 'Benefícios' no banco, mas têm
// submenu próprio (/BpoRh/Farmácia) — categoria VIRTUAL, só do frontend
const ehFarmacia = (servico) => /farm[aá]cia/i.test(servico.propriedade || '');
const CATEGORIA_FARMACIA = 'Farmácia';

const pontoSemApi = (servico) => {
  const pl = (servico.propriedade || '').toLowerCase();
  return pl.includes('aghora') || pl.includes('ahgora');
};

// tipo do provedor (mesmos rótulos do backend) a partir da propriedade — usado
// para abrir o painel já no sistema certo quando o cliente tem mais de um ponto.
const pontoTipo = (servico) => {
  const pl = (servico.propriedade || '').toLowerCase();
  if (pl.includes('ponto mais')) return 'pontomais';
  if (pl.includes('tiquetaque')) return 'tiquetaque';
  if (pl.includes('rhid')) return 'rhid';
  if (pl.includes('kairos')) return 'kairos';
  if (pl.includes('aghora') || pl.includes('ahgora')) return 'aghora';
  return '';
};

const BpoRhClientes = () => {
  // Quando acessada via /BpoRh/:categoria (submenu do sidebar), a página
  // trabalha travada nessa categoria — é a página de trabalho da categoria.
  const { categoria: categoriaRota } = useParams();
  const categoriaAtiva = categoriaRota ? decodeURIComponent(categoriaRota) : null;

  const [servicos, setServicos] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [mensagem, setMensagem] = useState('');

  // Ponte para toasts (react-toastify)
  useEffect(() => { notificar(mensagem); }, [mensagem]);

  const [filtros, setFiltros] = useState({
    cliente: [],
    operador: [],
    categoria: [],
    propriedade: [],
    busca: '',
  });

  // Senhas visíveis por id de serviço + valores já revelados pelo backend
  // (a listagem só informa `tem_senha` / `tem_api_token`)
  const [senhasVisiveis, setSenhasVisiveis] = useState([]);
  const [senhasReveladas, setSenhasReveladas] = useState({});
  const [tokensVisiveis, setTokensVisiveis] = useState([]);
  const [tokensRevelados, setTokensRevelados] = useState({});

  // Modal de cadastro/edição
  const [mostrarModal, setMostrarModal] = useState(false);
  const [formulario, setFormulario] = useState(FORM_VAZIO);
  // Campos que a operadora realmente editou — senha/api_token só entram no
  // payload se estiverem aqui, senão uma edição de rotina apagaria o segredo
  const [camposTocados, setCamposTocados] = useState([]);
  const [mostrarSenhaForm, setMostrarSenhaForm] = useState(false);
  const [mostrarApiToken, setMostrarApiToken] = useState(false);
  const [salvando, setSalvando] = useState(false);

  // Modal de exclusão
  const [servicoExcluir, setServicoExcluir] = useState(null);
  const [excluindo, setExcluindo] = useState(false);

  const carregarClientes = useCallback(() => {
    const accessToken = localStorage.getItem('access');
    return axios
      .get(`${process.env.REACT_APP_API_BASE}/api/bporh-clientes/`, {
        headers: { Authorization: `Bearer ${accessToken}` }
      })
      .then((res) => setClientes(res.data.clientes || []))
      .catch(() => {});
  }, []);

  const carregarServicos = useCallback(() => {
    setCarregando(true);
    const accessToken = localStorage.getItem('access');

    return axios
      .get(`${process.env.REACT_APP_API_BASE}/api/bporh-servicos/`, {
        headers: { Authorization: `Bearer ${accessToken}` }
      })
      .then((res) => {
        setServicos(res.data.servicos || []);
        setCarregando(false);
      })
      .catch((error) => {
        const errorMsg =
          error.response?.data?.erro ||
          error.response?.data?.mensagem ||
          error.message ||
          'Erro desconhecido ao carregar os serviços';
        setMensagem(`${errorMsg}`);
        setCarregando(false);
      });
  }, []);

  useEffect(() => {
    carregarClientes();
    carregarServicos();
  }, [carregarClientes, carregarServicos]);

  // Ao trocar de categoria pelo submenu, limpa os filtros da página anterior
  useEffect(() => {
    setFiltros({ cliente: [], operador: [], categoria: [], propriedade: [], busca: '' });
  }, [categoriaAtiva]);

  // Base de trabalho: com categoria ativa (rota do submenu), só os serviços dela.
  // Em 'Benefícios', VA e VT já têm controle próprio (Controle de VA / Controle
  // de VT): ficam de fora daqui, sobra só o que não tem tela (Alura, Wellhub…).
  const servicosBase = React.useMemo(() => {
    if (!categoriaAtiva) return servicos;
    // Farmácia é virtual: são os convênios de farmácia cadastrados em Benefícios
    if (categoriaAtiva === CATEGORIA_FARMACIA) {
      return servicos.filter(r => r.categoria === 'Benefícios' && ehFarmacia(r));
    }
    const daCategoria = servicos.filter(r => r.categoria === categoriaAtiva);
    if (['benefícios', 'va'].includes(categoriaAtiva.trim().toLowerCase())) {
      return daCategoria.filter(r => !ehVA(r) && !ehFarmacia(r)
        && !(r.propriedade || '').toLowerCase().includes('vale transporte')
        && !/\bvt\b/i.test(r.propriedade || ''));
    }
    return daCategoria;
  }, [servicos, categoriaAtiva]);

  // Opções únicas para os filtros
  const opcoesCliente = React.useMemo(() =>
    [...new Set(servicosBase.map(r => r.cliente).filter(Boolean))].sort(),
    [servicosBase]);

  const opcoesOperador = React.useMemo(() =>
    [...new Set(servicosBase.map(r => r.operador).filter(Boolean))].sort(),
    [servicosBase]);

  const opcoesCategoria = React.useMemo(() =>
    [...new Set(servicos.map(r => r.categoria).filter(Boolean))].sort(),
    [servicos]);

  const opcoesPropriedade = React.useMemo(() =>
    [...new Set(servicosBase.map(r => r.propriedade).filter(Boolean))].sort(),
    [servicosBase]);

  const servicosFiltrados = React.useMemo(() => {
    const busca = filtros.busca.trim().toLowerCase();
    return servicosBase.filter(r => {
      if (filtros.cliente.length > 0 && !filtros.cliente.includes(r.cliente)) return false;
      if (filtros.operador.length > 0 && !filtros.operador.includes(r.operador)) return false;
      if (filtros.categoria.length > 0 && !filtros.categoria.includes(r.categoria)) return false;
      if (filtros.propriedade.length > 0 && !filtros.propriedade.includes(r.propriedade)) return false;
      if (busca) {
        const alvo = [r.cliente, r.operador, r.categoria, r.propriedade, r.login, r.portal, r.email_reset]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (!alvo.includes(busca)) return false;
      }
      return true;
    });
  }, [servicosBase, filtros]);

  // Busca a senha no backend na primeira vez que é pedida e guarda em memória
  const obterSenha = (id) => {
    if (senhasReveladas[id] !== undefined) return Promise.resolve(senhasReveladas[id]);
    return buscarCredencial('servico', id, 'senha')
      .then((valor) => {
        setSenhasReveladas(prev => ({ ...prev, [id]: valor }));
        return valor;
      })
      .catch((error) => {
        setMensagem(`${error.response?.data?.mensagem || 'Não foi possível obter a senha'}`);
        return null;
      });
  };

  const alternarSenhaVisivel = (id) => {
    if (senhasVisiveis.includes(id)) {
      setSenhasVisiveis(prev => prev.filter(v => v !== id));
      return;
    }
    obterSenha(id).then((valor) => {
      if (valor !== null) setSenhasVisiveis(prev => [...prev, id]);
    });
  };

  // mesmo caminho da senha: revela sob demanda (com log de auditoria no backend)
  const obterToken = (id) => {
    if (tokensRevelados[id] !== undefined) return Promise.resolve(tokensRevelados[id]);
    return buscarCredencial('servico', id, 'api_token')
      .then((valor) => {
        setTokensRevelados(prev => ({ ...prev, [id]: valor }));
        return valor;
      })
      .catch((error) => {
        setMensagem(`${error.response?.data?.mensagem || 'Não foi possível obter o token'}`);
        return null;
      });
  };

  const alternarTokenVisivel = (id) => {
    if (tokensVisiveis.includes(id)) {
      setTokensVisiveis(prev => prev.filter(v => v !== id));
      return;
    }
    obterToken(id).then((valor) => {
      if (valor !== null) setTokensVisiveis(prev => [...prev, id]);
    });
  };

  const copiarTexto = (texto, rotulo) => {
    copiarParaClipboard(texto)
      .then(() => setMensagem(`${rotulo} copiado para a área de transferência`))
      .catch((e) => setMensagem(`${e.message || 'Não foi possível copiar'}`));
  };

  const copiarSenha = (id) => {
    obterSenha(id).then((valor) => {
      if (valor !== null) copiarTexto(valor, 'Senha');
    });
  };

  const abrirModalNovo = () => {
    // Farmácia não existe como categoria no banco: o novo serviço nasce em Benefícios
    setFormulario({ ...FORM_VAZIO,
      categoria: categoriaAtiva === CATEGORIA_FARMACIA ? 'Benefícios' : (categoriaAtiva || '') });
    setCamposTocados([]);
    setMostrarSenhaForm(false);
    setMostrarApiToken(false);
    setMostrarModal(true);
  };

  const abrirModalEdicao = (servico) => {
    // senha e api_token não vêm na listagem: os campos abrem vazios e só são
    // enviados se a operadora digitar algo (ver camposTocados)
    setFormulario({
      id: servico.id,
      cliente_id: servico.cliente_id || '',
      categoria: servico.categoria || '',
      propriedade: servico.propriedade || '',
      portal: servico.portal || '',
      login: servico.login || '',
      senha: '',
      email_reset: servico.email_reset || '',
      api_token: '',
      tem_senha: !!servico.tem_senha,
      tem_api_token: !!servico.tem_api_token,
    });
    setCamposTocados([]);
    setMostrarSenhaForm(false);
    setMostrarApiToken(false);
    setMostrarModal(true);
  };

  const fecharModal = () => {
    if (salvando) return;
    setMostrarModal(false);
    setFormulario(FORM_VAZIO);
    setCamposTocados([]);
  };

  const handleFormChange = (campo, valor) => {
    setFormulario(prev => ({ ...prev, [campo]: valor }));
    setCamposTocados(prev => (prev.includes(campo) ? prev : [...prev, campo]));
  };

  // Carrega a credencial atual no campo, para editar em cima do valor existente
  const revelarNoFormulario = (campo) => {
    if (!formulario.id) return;
    buscarCredencial('servico', formulario.id, campo)
      .then((valor) => setFormulario(prev => ({ ...prev, [campo]: valor })))
      .catch((error) =>
        setMensagem(`${error.response?.data?.mensagem || 'Não foi possível obter o valor atual'}`));
  };

  // Operador do cliente selecionado no formulário (somente leitura — vem do cadastro do cliente)
  const operadorDoFormulario = React.useMemo(() => {
    const c = clientes.find(cl => String(cl.id) === String(formulario.cliente_id));
    return c?.operador || '';
  }, [clientes, formulario.cliente_id]);

  const salvarServico = () => {
    if (!formulario.propriedade.trim()) {
      setMensagem('Informe a Propriedade (software/portal)');
      return;
    }
    if (!formulario.categoria.trim()) {
      setMensagem('Informe a Categoria');
      return;
    }

    setSalvando(true);
    const accessToken = localStorage.getItem('access');
    const headers = { Authorization: `Bearer ${accessToken}` };
    // Credenciais coladas de e-mail vêm com espaço/quebra de linha e o SMTP/API
    // recusa — por isso o trim em tudo.
    const payload = {
      cliente_id: formulario.cliente_id || null,
      categoria: formulario.categoria.trim(),
      propriedade: formulario.propriedade.trim(),
      portal: (formulario.portal || '').trim(),
      login: (formulario.login || '').trim(),
      email_reset: (formulario.email_reset || '').trim(),
    };
    // Só manda a credencial se foi digitada (num serviço novo, sempre).
    // Omitir a chave = manter o valor que já está no banco.
    if (!formulario.id || camposTocados.includes('senha')) {
      payload.senha = (formulario.senha || '').trim();
    }
    if (!formulario.id || camposTocados.includes('api_token')) {
      payload.api_token = (formulario.api_token || '').trim();
    }

    const requisicao = formulario.id
      ? axios.put(`${process.env.REACT_APP_API_BASE}/api/bporh-servicos/${formulario.id}/`, payload, { headers })
      : axios.post(`${process.env.REACT_APP_API_BASE}/api/bporh-servicos/`, payload, { headers });

    requisicao
      .then((res) => {
        setMensagem(`${res.data.mensagem || 'Serviço salvo com sucesso'}`);
        setSalvando(false);
        setMostrarModal(false);
        setFormulario(FORM_VAZIO);
        carregarServicos();
      })
      .catch((error) => {
        const errorMsg =
          error.response?.data?.mensagem ||
          error.response?.data?.erro ||
          'Erro ao salvar o serviço';
        setMensagem(`${errorMsg}`);
        setSalvando(false);
      });
  };

  const confirmarExclusao = () => {
    if (!servicoExcluir) return;
    setExcluindo(true);
    const accessToken = localStorage.getItem('access');

    axios
      .delete(`${process.env.REACT_APP_API_BASE}/api/bporh-servicos/${servicoExcluir.id}/`, {
        headers: { Authorization: `Bearer ${accessToken}` }
      })
      .then((res) => {
        setMensagem(`${res.data.mensagem || 'Serviço excluído com sucesso'}`);
        setExcluindo(false);
        setServicoExcluir(null);
        carregarServicos();
      })
      .catch((error) => {
        const errorMsg =
          error.response?.data?.mensagem ||
          error.response?.data?.erro ||
          'Erro ao excluir o serviço';
        setMensagem(`${errorMsg}`);
        setExcluindo(false);
      });
  };

  return (
    <div className="bporh-servicos-page">
      <div className="container-fluid mt-4 shadow p-4 rounded bg-white">
        {/* Cabeçalho */}
        <div className="header-wrapper d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
          <div className="d-flex align-items-center">
            <i className={`fas ${categoriaAtiva ? 'fa-folder-open' : 'fa-user-lock'} header-icon me-2`}></i>
            <h2 className="titulo-pagina m-0">
              {categoriaAtiva ? `BPO RH — ${categoriaAtiva}` : 'BPO RH — Serviços e Acessos'}
            </h2>
          </div>
          <button className="btn btn-primary" onClick={abrirModalNovo}>
            <i className="fas fa-plus me-2"></i>
            Novo Serviço
          </button>
        </div>

        {/* Filtros */}
        <div className="row mb-3 g-2">
          <div className="col-md-3">
            <label className="form-label fw-bold mb-0">Cliente</label>
            <MultiSelectDropdown
              options={opcoesCliente}
              selected={filtros.cliente}
              onChange={(vals) => setFiltros(prev => ({ ...prev, cliente: vals }))}
              placeholder="Todos os clientes"
            />
          </div>
          <div className="col-md-3">
            <label className="form-label fw-bold mb-0">Operador</label>
            <MultiSelectDropdown
              options={opcoesOperador}
              selected={filtros.operador}
              onChange={(vals) => setFiltros(prev => ({ ...prev, operador: vals }))}
              placeholder="Todos os operadores"
            />
          </div>
          {!categoriaAtiva && (
            <div className="col-md-3">
              <label className="form-label fw-bold mb-0">Categoria</label>
              <MultiSelectDropdown
                options={opcoesCategoria}
                selected={filtros.categoria}
                onChange={(vals) => setFiltros(prev => ({ ...prev, categoria: vals }))}
                placeholder="Todas as categorias"
              />
            </div>
          )}
          <div className="col-md-3">
            <label className="form-label fw-bold mb-0">Busca</label>
            <input
              type="text"
              className="form-control form-control-sm mt-1"
              placeholder="Buscar por login, portal, e-mail..."
              value={filtros.busca}
              onChange={(e) => setFiltros(prev => ({ ...prev, busca: e.target.value }))}
            />
          </div>
        </div>

        {/* Contagem */}
        <div className="d-flex align-items-center gap-2 mb-2 flex-wrap">
          <span className="badge bg-secondary">
            {servicosFiltrados.length} de {servicosBase.length} serviços
          </span>
          {(filtros.cliente.length > 0 || filtros.operador.length > 0 || filtros.categoria.length > 0 || filtros.propriedade.length > 0 || filtros.busca) && (
            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={() => setFiltros({ cliente: [], operador: [], categoria: [], propriedade: [], busca: '' })}
            >
              <i className="fas fa-eraser me-1"></i>
              Limpar filtros
            </button>
          )}
        </div>

        {/* Tabela */}
        {carregando ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status"></div>
            <p className="text-muted mt-2 mb-0">Carregando serviços...</p>
          </div>
        ) : servicosFiltrados.length === 0 ? (
          <div className="alert alert-info">
            <i className="fas fa-info-circle me-2"></i>
            Nenhum serviço encontrado com os filtros atuais.
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table table-striped table-hover align-middle">
              <thead>
                <tr>
                  <th><i className="fas fa-building me-2"></i>Cliente</th>
                  <th><i className="fas fa-user-tie me-2"></i>Operador</th>
                  {!categoriaAtiva && <th><i className="fas fa-tags me-2"></i>Categoria</th>}
                  <th><i className="fas fa-desktop me-2"></i>Propriedade</th>
                  <th><i className="fas fa-globe me-2"></i>Portal</th>
                  <th><i className="fas fa-user me-2"></i>Login</th>
                  <th><i className="fas fa-key me-2"></i>Senha</th>
                  <th><i className="fas fa-envelope me-2"></i>E-mail Reset</th>
                  <th>
                    <i className={`fas ${categoriaAtiva === 'Ponto' ? 'fa-stopwatch' : 'fa-plug'} me-2`}></i>
                    {categoriaAtiva === 'Ponto' ? 'API Ponto' : 'API Token'}
                  </th>
                  <th className="text-center">Ações</th>
                </tr>
              </thead>
              <tbody>
                {servicosFiltrados.map((servico) => (
                  <tr key={servico.id}>
                    <td className="fw-bold">{servico.cliente || '-'}</td>
                    <td>{servico.operador || '-'}</td>
                    {!categoriaAtiva && <td>{servico.categoria || '-'}</td>}
                    <td>{servico.propriedade || '-'}</td>
                    <td>
                      {servico.portal ? (
                        <a
                          href={servico.portal.startsWith('http') ? servico.portal : `https://${servico.portal}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-sm btn-outline-primary"
                          title={servico.portal}
                        >
                          <i className="fas fa-external-link-alt me-1"></i>
                          Abrir
                        </a>
                      ) : '-'}
                    </td>
                    <td>
                      {servico.login ? (
                        <span className="d-inline-flex align-items-center gap-1">
                          <span style={{ wordBreak: 'break-all' }}>{servico.login}</span>
                          <button
                            className="btn btn-sm btn-light p-1"
                            onClick={() => copiarTexto(servico.login, 'Login')}
                            title="Copiar login"
                          >
                            <i className="fas fa-copy text-muted"></i>
                          </button>
                        </span>
                      ) : '-'}
                    </td>
                    <td>
                      {servico.tem_senha ? (
                        <span className="d-inline-flex align-items-center gap-1">
                          <code>
                            {senhasVisiveis.includes(servico.id)
                              ? senhasReveladas[servico.id]
                              : '••••••••'}
                          </code>
                          <button
                            className="btn btn-sm btn-light p-1"
                            onClick={() => alternarSenhaVisivel(servico.id)}
                            title={senhasVisiveis.includes(servico.id) ? 'Ocultar senha' : 'Mostrar senha'}
                          >
                            <i className={`fas ${senhasVisiveis.includes(servico.id) ? 'fa-eye-slash' : 'fa-eye'} text-muted`}></i>
                          </button>
                          <button
                            className="btn btn-sm btn-light p-1"
                            onClick={() => copiarSenha(servico.id)}
                            title="Copiar senha"
                          >
                            <i className="fas fa-copy text-muted"></i>
                          </button>
                        </span>
                      ) : '-'}
                    </td>
                    <td style={{ wordBreak: 'break-all' }}>{servico.email_reset || '-'}</td>
                    <td>
                      {servico.tem_api_token ? (
                        <span className="d-inline-flex align-items-center gap-1">
                          <code style={{ wordBreak: 'break-all' }}>
                            {tokensVisiveis.includes(servico.id)
                              ? tokensRevelados[servico.id]
                              : '••••••••'}
                          </code>
                          <button
                            className="btn btn-sm btn-light p-1"
                            onClick={() => alternarTokenVisivel(servico.id)}
                            title={tokensVisiveis.includes(servico.id) ? 'Ocultar token' : 'Mostrar token'}
                          >
                            <i className={`fas ${tokensVisiveis.includes(servico.id) ? 'fa-eye-slash' : 'fa-eye'} text-muted`}></i>
                          </button>
                          <button
                            className="btn btn-sm btn-light p-1"
                            onClick={() => obterToken(servico.id).then(v => v !== null && copiarTexto(v, 'Token'))}
                            title="Copiar token"
                          >
                            <i className="fas fa-copy text-muted"></i>
                          </button>
                        </span>
                      ) : categoriaAtiva === 'Ponto' && pontoSemApi(servico) ? (
                        <span className="text-muted small">
                          <i className="fas fa-file-csv me-1"></i>
                          sem API — importar a planilha (CSV/Excel) no painel de Ponto
                        </span>
                      ) : <span className="text-muted">-</span>}
                    </td>
                    <td className="text-center" style={{ whiteSpace: 'nowrap' }}>
                      {categoriaAtiva === 'Ponto' && servico.cliente_id && pontoAtivo(servico) && (
                        <Link
                          to={`/BpoRh/Ponto/cliente/${servico.cliente_id}?sistema=${pontoTipo(servico)}`}
                          className="btn btn-sm btn-outline-success me-1"
                          title={`Painel de ponto (${servico.propriedade}) da ${servico.cliente} — batidas do dia e falta/sobra`}
                        >
                          <i className="fas fa-stopwatch me-1"></i>
                          Ponto
                        </Link>
                      )}
                      {categoriaAtiva === 'Vale Transporte' && servico.cliente_id && (
                        <Link
                          to={`/BpoRh/ValeTransporte/cliente/${servico.cliente_id}`}
                          className="btn btn-sm btn-outline-success me-1"
                          title={`Controle de vale transporte da ${servico.cliente} — valor, dias e recarga do mês`}
                        >
                          <i className="fas fa-bus me-1"></i>
                          VT
                        </Link>
                      )}
                      {categoriaAtiva === 'Benefícios' && servico.cliente_id && ehVA(servico) && (
                        <Link
                          to={`/BpoRh/Beneficios/cliente/${servico.cliente_id}`}
                          className="btn btn-sm btn-outline-success me-1"
                          title={`Controle de VA da ${servico.cliente} — valor, dias, feriados e recarga do mês`}
                        >
                          <i className="fas fa-utensils me-1"></i>
                          VA
                        </Link>
                      )}
                      {ehHomeOffice(categoriaAtiva) && servico.cliente_id && (
                        <Link
                          to={`/BpoRh/HomeOffice/cliente/${servico.cliente_id}`}
                          className="btn btn-sm btn-outline-success me-1"
                          title={`Auxílio home office da ${servico.cliente} — valor, dias, feriados e pedido do mês`}
                        >
                          <i className="fas fa-house-laptop me-1"></i>
                          Home office
                        </Link>
                      )}
                      <button
                        className="btn btn-sm btn-outline-primary me-1"
                        onClick={() => abrirModalEdicao(servico)}
                        title="Editar"
                      >
                        <i className="fas fa-edit"></i>
                      </button>
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => setServicoExcluir(servico)}
                        title="Excluir"
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* MODAL DE CADASTRO / EDIÇÃO */}
      {mostrarModal && (
        <div
          className="modal"
          tabIndex="-1"
          style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999 }}
        >
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content shadow-lg">
              <div className="modal-header" style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef' }}>
                <div>
                  <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                    <i className={`fas ${formulario.id ? 'fa-edit' : 'fa-plus'} me-2`}></i>
                    {formulario.id ? 'Editar Serviço' : 'Novo Serviço'}
                  </h5>
                  <small style={{ color: '#6c757d' }}>
                    {formulario.id ? 'Altere os dados do serviço abaixo' : 'Preencha os dados do novo serviço'}
                  </small>
                </div>
                <button type="button" className="btn-close" onClick={fecharModal} disabled={salvando}></button>
              </div>

              <div className="modal-body">
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label fw-bold">
                      <i className="fas fa-building me-2 text-primary"></i>
                      Cliente
                    </label>
                    <select
                      className="form-select"
                      value={formulario.cliente_id}
                      onChange={(e) => handleFormChange('cliente_id', e.target.value)}
                      disabled={salvando}
                    >
                      <option value="">— Sem cliente —</option>
                      {clientes.map(c => (
                        <option key={c.id} value={c.id}>{c.cliente}</option>
                      ))}
                    </select>
                    <small className="text-muted">
                      Clientes são gerenciados na página <strong>Clientes</strong> (operador e API).
                    </small>
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-bold">
                      <i className="fas fa-user-tie me-2 text-primary"></i>
                      Operador
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      value={operadorDoFormulario}
                      placeholder="Definido no cadastro do cliente"
                      readOnly
                      disabled
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-bold">
                      <i className="fas fa-tags me-2 text-primary"></i>
                      Categoria <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      list="lista-categorias-bporh"
                      value={formulario.categoria}
                      onChange={(e) => handleFormChange('categoria', e.target.value)}
                      placeholder="Ex: Ponto, Benefícios..."
                      disabled={salvando}
                    />
                    <datalist id="lista-categorias-bporh">
                      {opcoesCategoria.map(c => <option key={c} value={c} />)}
                    </datalist>
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-bold">
                      <i className="fas fa-desktop me-2 text-primary"></i>
                      Propriedade <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      list="lista-propriedades-bporh"
                      value={formulario.propriedade}
                      onChange={(e) => handleFormChange('propriedade', e.target.value)}
                      placeholder="Ex: Pontomais, Feedz..."
                      disabled={salvando}
                    />
                    <datalist id="lista-propriedades-bporh">
                      {opcoesPropriedade.map(p => <option key={p} value={p} />)}
                    </datalist>
                  </div>

                  <div className="col-md-12">
                    <label className="form-label fw-bold">
                      <i className="fas fa-globe me-2 text-primary"></i>
                      Portal (URL) — onde é feito
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      value={formulario.portal}
                      onChange={(e) => handleFormChange('portal', e.target.value)}
                      placeholder="https://..."
                      disabled={salvando}
                    />
                  </div>

                  <div className="col-md-4">
                    <label className="form-label fw-bold">
                      <i className="fas fa-user me-2 text-primary"></i>
                      Login
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      value={formulario.login}
                      onChange={(e) => handleFormChange('login', e.target.value)}
                      placeholder="Usuário / e-mail de acesso"
                      disabled={salvando}
                    />
                  </div>

                  <div className="col-md-4">
                    <label className="form-label fw-bold">
                      <i className="fas fa-key me-2 text-primary"></i>
                      Senha
                    </label>
                    <div className="input-group">
                      <input
                        type={mostrarSenhaForm ? 'text' : 'password'}
                        className="form-control"
                        value={formulario.senha}
                        onChange={(e) => handleFormChange('senha', e.target.value)}
                        placeholder={formulario.tem_senha ? 'Deixe em branco para manter a atual' : 'Senha de acesso'}
                        disabled={salvando}
                        autoComplete="new-password"
                      />
                      {formulario.tem_senha && (
                        <button
                          type="button"
                          className="btn btn-outline-secondary"
                          onClick={() => revelarNoFormulario('senha')}
                          title="Carregar a senha atual para editar"
                          tabIndex={-1}
                        >
                          <i className="fas fa-download"></i>
                        </button>
                      )}
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() => setMostrarSenhaForm(v => !v)}
                        tabIndex={-1}
                      >
                        <i className={`fas ${mostrarSenhaForm ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                      </button>
                    </div>
                  </div>

                  <div className="col-md-4">
                    <label className="form-label fw-bold">
                      <i className="fas fa-envelope me-2 text-primary"></i>
                      E-mail Reset
                    </label>
                    <input
                      type="email"
                      className="form-control"
                      value={formulario.email_reset}
                      onChange={(e) => handleFormChange('email_reset', e.target.value)}
                      placeholder="E-mail de recuperação"
                      disabled={salvando}
                    />
                  </div>

                  <div className="col-md-12">
                    <label className="form-label fw-bold">
                      <i className={`fas ${categoriaAtiva === 'Ponto' ? 'fa-stopwatch' : 'fa-key'} me-2 text-primary`}></i>
                      {categoriaAtiva === 'Ponto' ? 'API Ponto' : 'API Token'}
                    </label>
                    <div className="input-group">
                      <input
                        type={mostrarApiToken ? 'text' : 'password'}
                        className="form-control"
                        value={formulario.api_token}
                        onChange={(e) => handleFormChange('api_token', e.target.value)}
                        placeholder={formulario.tem_api_token
                          ? 'Deixe em branco para manter o token atual'
                          : 'Token de API do serviço (se houver)'}
                        disabled={salvando}
                        autoComplete="new-password"
                      />
                      {formulario.tem_api_token && (
                        <button
                          type="button"
                          className="btn btn-outline-secondary"
                          onClick={() => revelarNoFormulario('api_token')}
                          title="Carregar o token atual para editar"
                          tabIndex={-1}
                        >
                          <i className="fas fa-download"></i>
                        </button>
                      )}
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() => setMostrarApiToken(v => !v)}
                        tabIndex={-1}
                      >
                        <i className={`fas ${mostrarApiToken ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="modal-footer bg-light">
                <button type="button" className="btn btn-secondary" onClick={fecharModal} disabled={salvando}>
                  <i className="fas fa-times me-2"></i>
                  Cancelar
                </button>
                <button type="button" className="btn btn-success" onClick={salvarServico} disabled={salvando}>
                  {salvando ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Salvando...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-save me-2"></i>
                      {formulario.id ? 'Salvar Alterações' : 'Cadastrar'}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MODAL DE CONFIRMAÇÃO DE EXCLUSÃO */}
      {servicoExcluir && (
        <div
          className="modal"
          tabIndex="-1"
          style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999 }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content shadow-lg">
              <div className="modal-header" style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef' }}>
                <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                  <i className="fas fa-exclamation-triangle me-2 text-danger"></i>
                  Confirmar Exclusão
                </h5>
                <button type="button" className="btn-close" onClick={() => setServicoExcluir(null)} disabled={excluindo}></button>
              </div>
              <div className="modal-body">
                <p className="mb-2">Tem certeza que deseja excluir este serviço?</p>
                <div className="alert alert-warning mb-0">
                  <strong>{servicoExcluir.propriedade}</strong>
                  {servicoExcluir.categoria && <> — {servicoExcluir.categoria}</>}
                  {servicoExcluir.cliente && (
                    <div className="small mt-1">
                      <i className="fas fa-building me-1"></i>
                      Cliente: {servicoExcluir.cliente}
                    </div>
                  )}
                </div>
              </div>
              <div className="modal-footer bg-light">
                <button type="button" className="btn btn-secondary" onClick={() => setServicoExcluir(null)} disabled={excluindo}>
                  <i className="fas fa-times me-2"></i>
                  Cancelar
                </button>
                <button type="button" className="btn btn-danger" onClick={confirmarExclusao} disabled={excluindo}>
                  {excluindo ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Excluindo...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-trash me-2"></i>
                      Excluir
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BpoRhClientes;
